import { ArrowLeftOutlined, EyeOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Descriptions, Empty, Input, message, Modal, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import salesInvoicesService, { type SalesInvoice, type SalesInvoiceItem } from '../../services/salesInvoices';

const { Title, Text } = Typography;

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);

const formatDateTime = (value: string) => new Date(value).toLocaleString('vi-VN');

const normalizeSearchText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd');

export default function LichSuBanHangPage() {
  const navigate = useNavigate();
  const employeeName = localStorage.getItem('user_name')?.trim() || 'NGUYEN VAN A';
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs, Dayjs] | null>(() => {
    const today = dayjs();
    return [today, today];
  });
  const [dailyRevenue, setDailyRevenue] = useState(0);
  const [dailyInvoiceCount, setDailyInvoiceCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<SalesInvoice | null>(null);

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    setInvoices([]);
    setDailyRevenue(0);
    setDailyInvoiceCount(0);

    salesInvoicesService.getByDateRange(
      selectedDateRange?.[0].format('YYYY-MM-DD'),
      selectedDateRange?.[1].format('YYYY-MM-DD')
    ).then((result) => {
      if (!isCurrent) return;
      setInvoices(result.invoices);
      setDailyRevenue(result.revenue);
      setDailyInvoiceCount(result.count);
    }).catch((error: unknown) => {
      if (isCurrent) {
        message.error(error instanceof Error ? error.message : 'Không thể tải lịch sử bán hàng');
      }
    }).finally(() => {
      if (isCurrent) setIsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [selectedDateRange]);

  const normalizedSearchText = normalizeSearchText(searchText.trim());
  const filteredInvoices = invoices.filter((invoice) =>
    normalizeSearchText([
      invoice.id,
      invoice.tableName,
      ...invoice.items.map((item) => item.name),
    ].join(' ')).includes(normalizedSearchText)
  );

  const printInvoice = (invoice: SalesInvoice) => {
    const printWindow = window.open('', '_blank', 'width=420,height=700');
    if (!printWindow) {
      message.warning('Trình duyệt đã chặn cửa sổ in. Vui lòng cho phép popup và thử lại.');
      return;
    }

    const escapeHtml = (value: string) =>
      value.replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character] || character);
    const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL}/image/logo-cuong-hoai.png`;
    const bankQrCodeUrl = `${window.location.origin}${process.env.PUBLIC_URL}/image/qrcode-ngan-hang.jpg`;
    const receiptHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Hóa đơn ${escapeHtml(invoice.id)}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #000;
              font-family: 'Courier New', monospace;
              font-size: 13px;
              font-weight: 700;
              line-height: 1.45;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body {
              width: 80mm;
              padding: 3mm 5mm 3mm 7mm;
              box-sizing: border-box;
            }
            .receipt { width: 100%; overflow-wrap: anywhere; }
            .title, .center { text-align: center; font-weight: 700; }
            .title { font-size: 16px; }
            .logo {
              display: block;
              max-width: 100%;
              max-height: 22mm;
              object-fit: contain;
              margin: 0 auto 3mm;
            }
            .bank-qr-code {
              display: block;
              width: 42mm;
              max-width: 100%;
              height: auto;
              margin: 3mm auto 0;
            }
            .divider { border-top: 1px dashed #000; margin: 8px 0; }
            .item { margin: 5px 0; }
            .item-name { font-weight: 700; overflow-wrap: anywhere; }
            .item-meta, .total {
              display: flex;
              justify-content: space-between;
              gap: 8px;
            }
            .total { font-size: 14px; font-weight: 700; }
          </style>
        </head>
        <body>
          <div class="receipt">
            <img class="logo" src="${logoUrl}" alt="Logo Làng Nướng Cương Hoài">
            <div class="title">LÀNG NƯỚNG CƯƠNG HOÀI</div>
            <div class="center">${escapeHtml(invoice.tableName)}</div>
            <div>${escapeHtml(formatDateTime(invoice.paidAt))}</div>
            <div>Mã hóa đơn: ${escapeHtml(invoice.id)}</div>
            <div class="divider"></div>
            ${invoice.items.map((item) => `
              <div class="item">
                <div class="item-name">${escapeHtml(item.name)}</div>
                <div class="item-meta">
                  <span>${item.quantity} x ${formatMoney(item.price)}</span>
                  <span>${formatMoney(item.price * item.quantity)}</span>
                </div>
              </div>
            `).join('')}
            <div class="divider"></div>
            <div class="total"><strong>Tổng tiền</strong><strong>${formatMoney(invoice.total)}</strong></div>
            <div class="center" style="margin-top: 8px">Quét mã QR để chuyển khoản</div>
            <img class="bank-qr-code" src="${bankQrCodeUrl}" alt="Mã QR thanh toán ngân hàng">
            <div class="center" style="margin-top: 14px">Cảm ơn quý khách!</div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const columns: ColumnsType<SalesInvoice> = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => id,
    },
    {
      title: 'Thời gian thanh toán',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: formatDateTime,
    },
    {
      title: 'Bàn',
      dataIndex: 'tableName',
      key: 'tableName',
    },
    {
      title: 'Số lượng món',
      key: 'quantity',
      render: (_, invoice) => invoice.items.reduce((total, item) => total + item.quantity, 0),
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: formatMoney,
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, invoice) => (
        <Button
          type="link"
          icon={<EyeOutlined style={{ fontSize: '200%' }} />}
          onClick={() => setSelectedInvoice(invoice)}
        >
          Chi tiết
        </Button>
      ),
    },
  ];

  const itemColumns: ColumnsType<SalesInvoiceItem> = [
    { title: 'Món', dataIndex: 'name', key: 'name' },
    { title: 'Đơn giá', dataIndex: 'price', key: 'price', align: 'right', render: formatMoney },
    { title: 'Số lượng', dataIndex: 'quantity', key: 'quantity', align: 'right' },
    {
      title: 'Thành tiền',
      key: 'subtotal',
      align: 'right',
      render: (_, item) => formatMoney(item.price * item.quantity),
    },
  ];

  return (
    <div className="banhang-page">
      <div className="banhang-header">
        <div className="banhang-header-main">
          <Title level={2} style={{ margin: 0 }}>Lịch sử bán hàng</Title>
          <Text type="secondary">
            {selectedDateRange
              ? `Doanh thu từ ${selectedDateRange[0].format('DD/MM/YYYY')} đến ${selectedDateRange[1].format('DD/MM/YYYY')}`
              : 'Doanh thu tất cả ngày'}:
            {' '}{formatMoney(dailyRevenue)} · {dailyInvoiceCount} hóa đơn
          </Text>
        </div>
        <Space wrap>
          <Text strong>Nhân viên: {employeeName}</Text>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/banhang')}>
            Quay lại bán hàng
          </Button>
        </Space>
      </div>

      <Card className="table-panel">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <DatePicker.RangePicker
              value={selectedDateRange}
              onChange={(dates) => setSelectedDateRange(
                dates?.[0] && dates[1] ? [dates[0], dates[1]] : null
              )}
              allowClear
              placeholder={['Ngày bắt đầu', 'Ngày kết thúc']}
            />
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Tìm theo mã hóa đơn, bàn hoặc tên món"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              style={{ maxWidth: 420 }}
            />
          </Space>
          <Table<SalesInvoice>
            rowKey="id"
            columns={columns}
            dataSource={filteredInvoices}
            locale={{ emptyText: <Empty description={searchText ? 'Không tìm thấy hóa đơn' : 'Chưa có hóa đơn đã thanh toán'} /> }}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
            scroll={{ x: 760 }}
            loading={isLoading}
          />
        </Space>
      </Card>

      <Modal
        title={`Hóa đơn ${selectedInvoice?.id || ''}`}
        open={Boolean(selectedInvoice)}
        onCancel={() => setSelectedInvoice(null)}
        footer={[
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            disabled={!selectedInvoice}
            onClick={() => selectedInvoice && printInvoice(selectedInvoice)}
          >
            In lại hóa đơn
          </Button>,
          <Button key="close" onClick={() => setSelectedInvoice(null)}>Đóng</Button>,
        ]}
        width={760}
      >
        {selectedInvoice && (
          <>
            <Descriptions size="small" column={1} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Bàn">{selectedInvoice.tableName}</Descriptions.Item>
              <Descriptions.Item label="Thanh toán">{formatDateTime(selectedInvoice.paidAt)}</Descriptions.Item>
            </Descriptions>
            <Table<SalesInvoiceItem>
              rowKey="id"
              columns={itemColumns}
              dataSource={selectedInvoice.items}
              pagination={false}
              size="small"
              scroll={{ x: 520 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 16 }}>
              <Text strong>Tổng tiền</Text>
              <Text strong>{formatMoney(selectedInvoice.total)}</Text>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}